"use client";
import React, {
  FocusEvent,
  ForwardedRef,
  forwardRef,
  useCallback,
  useId,
} from "react";

import { Textarea, TextAreaProps } from "@nextui-org/react";

import FormWrapper from "@/ui/Forms/FormWrapper";

export interface FormTextareaProps extends TextAreaProps {
  label?: string;
  error?: string;
  autoSelect?: boolean;
  required?: boolean;
  labelRightComponent?: React.ReactNode;
  errorClassName?: string;
}

const classNames = {
  base: "form-textarea-base no-focus-ring",
  mainWrapper: "form-textarea-wrapper",
  input: "form-textarea-inner",
  innerWrapper: "form-textarea-wrapper",
  inputWrapper: "form-textarea-container",
  errorMessage: "form-error-message",
};
/**
 * Universal form textarea component with consistent styling.
 */
const FormTextarea = (
  {
    label,
    error,
    autoSelect,
    required = false,
    labelRightComponent,
    onFocus,
    className,
    errorClassName,
    ...textareaProps
  }: FormTextareaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) => {
  const inputId = useId();

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLTextAreaElement>) => {
      if (autoSelect) {
        event.currentTarget.select();
      }

      if (onFocus) {
        onFocus(event);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [autoSelect, onFocus],
  );

  return (
    <FormWrapper
      label={label}
      error={error}
      required={required}
      labelRight={labelRightComponent}
      errorClassName={errorClassName}
    >
      <Textarea
        ref={ref}
        id={inputId}
        onFocus={handleFocus}
        isInvalid={!!error}
        disableAnimation={true}
        classNames={classNames}
        className={className}
        {...textareaProps}
      />
    </FormWrapper>
  );
};

export default forwardRef(FormTextarea);
